'use client';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useChart } from '@/contexts/chart-context';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import { Sales } from '@/lib/api/SalesRoute';
import { OIL_NAME_COLORS } from '@/constants/fuel-colors';
import HighchartsReact from 'highcharts-react-official';
import { ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

// Highcharts initialization
const initializeHighcharts = (Highcharts: any) => {
  if (!Highcharts) return;

  Highcharts.setOptions({
    lang: {
      thousandsSep: ',',
      noData: '표시될 데이터가 없습니다.',
    },
  });
};

type ChartType = 'oil' | 'sale-type';
type ChartViewType = 'HOURLY' | 'DAILY' | 'MONTHLY' | 'DAY_OF_WEEK';




interface SalesData {
  groupField: string | number;
  oilType?: string;
  oilName?: string;
  saleType?: string;
  saleTypeName?: string;
  amt: number;
  qty: number;
  cnt: number;
}

const VIEW_TYPE_LABELS = {
  HOURLY: '시간별',
  DAILY: '일별',
  MONTHLY: '월별',
  DAY_OF_WEEK: '요일별',
};

interface PieChartData {
  oilData: Array<{
    name: string;
    amt: number;
    qty: number;
    cnt: number;
  }>;
  saleTypeData: Array<{
    name: string;
    amt: number;
    qty: number;
    cnt: number;
  }>;
}

interface SalesChartProps {
  startDate: string;
  endDate: string;
  onPieDataChange?: (data: PieChartData) => void;
}

export function SalesChart({
  startDate,
  endDate,
  onPieDataChange,
}: SalesChartProps) {
  const { Highcharts, isHighchartsLoaded } = useChart();
  const { toast } = useToast();
  const [chartType, setChartType] = useState<ChartType>('oil');
  const [chartViewType, setChartViewType] = useState<ChartViewType>('HOURLY');
  const [chartOptions, setChartOptions] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const getXAxisCategories = (viewType: ChartViewType) => {
    switch (viewType) {
      case 'HOURLY':
        return Array.from({ length: 24 }, (_, i) => i.toString());
      case 'DAILY':
        return Array.from({ length: 31 }, (_, i) => (i + 1).toString());
      case 'MONTHLY':
        return Array.from({ length: 12 }, (_, i) => (i + 1).toString());
      case 'DAY_OF_WEEK':
        return ['일', '월', '화', '수', '목', '금', '토'];
    }
  };

  const createChartData = (
    data: SalesData[],
    type: ChartType,
    viewType: ChartViewType
  ) => {
    const categories = getXAxisCategories(viewType);
    const seriesData: any[] = [];

    if (data.length === 0) {
      // 빈 데이터일 때
      const emptyPoints = categories.map((_, index) => ({ x: index, y: 0 }));
      seriesData.push(
        {
          name: '데이터 없음 (금액)',
          type: 'column',
          data: emptyPoints,
          tooltip: { valueSuffix: ' ₩' },
        },
        {
          name: '데이터 없음 (수량)',
          type: 'spline',
          yAxis: 1,
          data: emptyPoints,
        },
        {
          name: '데이터 없음 (건수)',
          type: 'spline',
          yAxis: 2,
          data: emptyPoints,
          dashStyle: 'shortdot',
          tooltip: { valueSuffix: ' 건' },
        }
      );
      return seriesData;
    }

    // 데이터가 있을 때
    const groupField = type === 'oil' ? 'oilType' : 'saleType';
    const nameField = type === 'oil' ? 'oilName' : 'saleTypeName';

    const uniqueTypes = [...new Set(data.map(item => item[groupField]))];

    uniqueTypes.forEach(typeValue => {
      const typeName =
        data.find(d => d[groupField] === typeValue)?.[nameField] || typeValue;

      const amtPoints: any[] = [];
      const qtyPoints: any[] = [];
      const cntPoints: any[] = [];

      categories.forEach((category, index) => {
        // 요일별의 경우 groupField가 0~6 숫자로 오므로 index와 비교
        // 다른 경우는 groupField와 category를 비교
        const point = data.find(
          item =>
            item[groupField] === typeValue &&
            (viewType === 'DAY_OF_WEEK'
              ? item.groupField == index
              : item.groupField == category)
        );

        amtPoints.push({ x: index, y: point?.amt || 0 });
        qtyPoints.push({ x: index, y: point?.qty || 0 });
        cntPoints.push({ x: index, y: point?.cnt || 0 });
      });

      // 유종별 색상 적용 (유종 차트의 금액 부분에만)
      const color =
        type === 'oil' && typeName
          ? OIL_NAME_COLORS[typeName] || undefined
          : undefined;

      seriesData.push(
        {
          name: `${typeName} 금액`,
          type: 'column',
          data: amtPoints,
          tooltip: { valueSuffix: ' ₩' },
          color: color, // 유종별 색상 적용
        },
        {
          name: `${typeName} 수량`,
          type: 'spline',
          yAxis: 1,
          data: qtyPoints,
        },
        {
          name: `${typeName} 건수`,
          type: 'spline',
          yAxis: 2,
          data: cntPoints,
          dashStyle: 'shortdot',
          tooltip: { valueSuffix: ' 건' },
        }
      );
    });

    return seriesData;
  };

  const loadPieData = useCallback(async () => {
    if (!isHighchartsLoaded || !Highcharts || !onPieDataChange) return;

    try {
      // Fetch oil data for pie chart
      const oilResponse = await apiClient.get('sales/timesale/chart/oil', {
        searchParams: {
          startDate,
          endDate,
          chartViewType, // Use current chart view type
        },
      });

      const oilApiResponse =
        (await oilResponse.json()) as Sales.GetTimeSaleOilChart.ResponseBody;

      // Fetch sale type data for pie chart
      const saleTypeResponse = await apiClient.get(
        'sales/timesale/chart/sale-type',
        {
          searchParams: {
            startDate,
            endDate,
            chartViewType, // Use current chart view type
          },
        }
      );

      const saleTypeApiResponse =
        (await saleTypeResponse.json()) as Sales.GetTimeSaleSaleTypeChart.ResponseBody;

      // Aggregate oil data by type
      const oilAggregated = new Map<
        string,
        { name: string; amt: number; qty: number; cnt: number }
      >();
      if (oilApiResponse.result && oilApiResponse.result.length > 0) {
        oilApiResponse.result.forEach(item => {
          const key = item.oilType || 'unknown';
          const existing = oilAggregated.get(key) || {
            name: item.oilName || key,
            amt: 0,
            qty: 0,
            cnt: 0,
          };
          existing.amt += item.amt || 0;
          existing.qty += item.qty || 0;
          existing.cnt += item.cnt || 0;
          oilAggregated.set(key, existing);
        });
      }

      // Aggregate sale type data by type
      const saleTypeAggregated = new Map<
        string,
        { name: string; amt: number; qty: number; cnt: number }
      >();
      if (saleTypeApiResponse.result && saleTypeApiResponse.result.length > 0) {
        saleTypeApiResponse.result.forEach(item => {
          const key = item.saleType || 'unknown';
          const existing = saleTypeAggregated.get(key) || {
            name: item.saleTypeName || key,
            amt: 0,
            qty: 0,
            cnt: 0,
          };
          existing.amt += item.amt || 0;
          existing.qty += item.qty || 0;
          existing.cnt += item.cnt || 0;
          saleTypeAggregated.set(key, existing);
        });
      }

      onPieDataChange({
        oilData: Array.from(oilAggregated.values()),
        saleTypeData: Array.from(saleTypeAggregated.values()),
      });
    } catch {
      toast({
        title: '파이 차트 데이터 로드 실패',
        description: '파이 차트 데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    }
  }, [
    isHighchartsLoaded,
    Highcharts,
    startDate,
    endDate,
    chartViewType,
    onPieDataChange,
    toast,
  ]);

  const loadChartData = useCallback(async () => {
    if (!isHighchartsLoaded || !Highcharts) return;

    try {
      setIsLoading(true);

      let salesData: SalesData[] = [];

      if (chartType === 'oil') {
        try {
          const response = await apiClient.get('sales/timesale/chart/oil', {
            searchParams: {
              startDate,
              endDate,
              chartViewType,
            },
          });

          const apiResponse =
            (await response.json()) as Sales.GetTimeSaleOilChart.ResponseBody;

          if (apiResponse.result && apiResponse.result.length > 0) {
            salesData = apiResponse.result.map(item => ({
              groupField: item.groupField || '0',
              oilType: item.oilType || '',
              oilName: item.oilName || '',
              amt: item.amt || 0,
              qty: item.qty || 0,
              cnt: item.cnt || 0,
            }));
          }
        } catch {
          toast({
            title: '유종 차트 데이터 로드 실패',
            description: '유종 차트 데이터를 불러오는 중 오류가 발생했습니다',
            variant: 'destructive',
          });
        }
      } else {
        try {
          const response = await apiClient.get(
            'sales/timesale/chart/sale-type',
            {
              searchParams: {
                startDate,
                endDate,
                chartViewType,
              },
            }
          );

          const apiResponse =
            (await response.json()) as Sales.GetTimeSaleSaleTypeChart.ResponseBody;

          if (apiResponse.result && apiResponse.result.length > 0) {
            salesData = apiResponse.result.map(item => ({
              groupField: item.groupField || '0',
              saleType: item.saleType || '',
              saleTypeName: item.saleTypeName || '',
              amt: item.amt || 0,
              qty: item.qty || 0,
              cnt: item.cnt || 0,
            }));
          }
        } catch {
          toast({
            title: '판매구분 차트 데이터 로드 실패',
            description:
              '판매구분 차트 데이터를 불러오는 중 오류가 발생했습니다',
            variant: 'destructive',
          });
        }
      }

      const categories = getXAxisCategories(chartViewType);
      const seriesData = createChartData(salesData, chartType, chartViewType);

      const options: any = {
        chart: {
          // @ts-ignore
          zoomType: 'xy',
          spacing: [30, 0, 0, 0],
        },
        title: {
          text: '',
          align: 'left',
        },
        xAxis: [
          {
            categories,
            crosshair: true,
          },
        ],
        yAxis: [
          {
            gridLineWidth: 0,
            title: {
              text: '금액',
              rotation: 0,
            },
            labels: {
              format: '{value} ₩',
              style: { color: '#7cb5ec' },
            },
          },
          {
            title: {
              text: '수량',
              rotation: 0,
              style: { color: '#90ed7d' },
            },
            labels: {
              format: '{value}',
              style: { color: '#90ed7d' },
            },
            opposite: true,
          },
          {
            gridLineWidth: 0,
            title: {
              text: '건수',
              rotation: 0,
              style: { color: '#434348' },
            },
            labels: {
              format: '{value} 건',
              style: { color: '#434348' },
            },
            opposite: true,
          },
        ],
        tooltip: {
          shared: true,
        },
        legend: {
          layout: 'vertical',
          align: 'right',
          verticalAlign: 'middle',
          backgroundColor: 'rgba(255,255,255,0.25)',
        },
        series: seriesData,
        responsive: {
          rules: [
            {
              condition: { maxWidth: 500 },
              chartOptions: {
                legend: {
                  floating: false,
                  layout: 'horizontal',
                  align: 'center',
                  verticalAlign: 'bottom',
                  x: 0,
                  y: 0,
                },
                yAxis: [
                  {
                    labels: { align: 'right', x: 0, y: -6 },
                    showLastLabel: false,
                  },
                  {
                    labels: { align: 'left', x: 0, y: -6 },
                    showLastLabel: false,
                  },
                  { visible: false },
                ],
              },
            },
          ],
        },
        credits: { enabled: false },
        exporting: {
          buttons: {
            contextButton: { y: -30 },
          },
        },
      };

      setChartOptions(options);
    } catch {
      toast({
        title: '차트 데이터 로드 실패',
        description: '차트 데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    chartType,
    chartViewType,
    isHighchartsLoaded,
    Highcharts,
    startDate,
    endDate,
    toast,
  ]);

  useEffect(() => {
    if (isHighchartsLoaded && Highcharts) {
      initializeHighcharts(Highcharts);
    }
  }, [isHighchartsLoaded, Highcharts]);

  useEffect(() => {
    if (isHighchartsLoaded && Highcharts) {
      loadChartData();
      loadPieData();
    }
  }, [
    chartType,
    chartViewType,
    startDate,
    endDate,
    isHighchartsLoaded,
    Highcharts,
    loadChartData,
    loadPieData,
  ]);

  useEffect(() => {
    const handleRefresh = () => {
      if (isHighchartsLoaded && Highcharts) {
        loadChartData();
        loadPieData();
      }
    };

    window.addEventListener('dashboard-refresh', handleRefresh);
    return () => window.removeEventListener('dashboard-refresh', handleRefresh);
  }, [loadChartData, loadPieData, isHighchartsLoaded, Highcharts]);

  return (
    <div>
      <Tabs
        value={chartType}
        onValueChange={value => setChartType(value as ChartType)}
      >
        <div className='flex items-center justify-between mb-1'>
          <TabsList className='bg-transparent'>
            <TabsTrigger
              value='oil'
              className='data-[state=active]:bg-[#2d353c] data-[state=active]:text-white'
            >
              유종
            </TabsTrigger>
            <TabsTrigger
              value='sale-type'
              className='data-[state=active]:bg-[#2d353c] data-[state=active]:text-white'
            >
              판매구분
            </TabsTrigger>
          </TabsList>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='outline' size='sm'>
                차트 집계 변경
                <ChevronDown className='ml-1 h-3 w-3' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {Object.entries(VIEW_TYPE_LABELS).map(([key, label]) => (
                <DropdownMenuItem
                  key={key}
                  onClick={() => setChartViewType(key as ChartViewType)}
                >
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <TabsContent value='oil' className='mt-0'>
          <Card>
            <CardContent className='p-2 rounded'>
              <div>
                {!isHighchartsLoaded || isLoading ? (
                  <div className='flex items-center justify-center h-full'>
                    <div className='text-gray-500'>
                      {!isHighchartsLoaded
                        ? 'Highcharts 초기화 중...'
                        : '데이터 로딩 중...'}
                    </div>
                  </div>
                ) : (
                  <HighchartsReact
                    ref={chartRef}
                    highcharts={Highcharts}
                    options={chartOptions}
                    containerProps={{ style: { height: '100%' } }}
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='sale-type' className='mt-0'>
          <Card>
            <CardContent className='p-2 rounded'>
              <div>
                {!isHighchartsLoaded || isLoading ? (
                  <div className='flex items-center justify-center h-full'>
                    <div className='text-gray-500'>
                      {!isHighchartsLoaded
                        ? 'Highcharts 초기화 중...'
                        : '데이터 로딩 중...'}
                    </div>
                  </div>
                ) : (
                  <HighchartsReact
                    ref={chartRef}
                    highcharts={Highcharts}
                    options={chartOptions}
                    containerProps={{ style: { height: '100%' } }}
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
